// Profile personalization API client (web). Wraps the shared Django endpoints
// the mobile profile uses: /api/profile/ (+ preferences, memberships, signals)
// and /api/weekly-summary/. Shapes mirror backend views_profile.py /
// views_signals.py exactly.

import axios from 'axios';
import API_BASE_URL from '../config';

const BASE_URL = `${API_BASE_URL}/api`;

const authHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) { window.location.href = '/login'; throw new Error('No access token'); }
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
};
const on401 = (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  }
};

// GET /api/profile/ → profile object
export const getProfile = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/profile/`, { headers: authHeaders() });
    return res.data.profile || res.data;
  } catch (err) { on401(err); throw err; }
};

// PATCH /api/profile/ → updated profile
export const updateProfile = async (patch) => {
  try {
    const res = await axios.patch(`${BASE_URL}/profile/`, patch, { headers: authHeaders() });
    return res.data.profile || res.data;
  } catch (err) { on401(err); throw err; }
};

// The five notification categories, loudest first. One per Android channel the
// mobile app creates (frontend_mobile/.../notificationService.ts) and per
// CHANNEL_* in backend/api/notification_service.py. The web has no channels of
// its own, so here the switch is the only thing standing between a user and a
// push: it stops the server sending rather than the phone showing.
export const NOTIFICATION_CATEGORIES = [
  { key: 'notif_reminders', label: 'Reminders',
    description: 'A task is due, or is about to be. Makes a sound.' },
  { key: 'notif_routines', label: 'Routines',
    description: 'Repeating habits from your Daily list. Makes a sound.' },
  { key: 'notif_nearby', label: 'Nearby shops',
    description: 'You are near a shop stocking something on your list. Silent.' },
  { key: 'notif_deals', label: 'Deals',
    description: 'Prices and coupons at shops near you. Silent.' },
  { key: 'notif_updates', label: 'Updates',
    description: 'Daily and weekly summaries, and announcements. Silent.' },
];

// GET /api/profile/preferences/ → [{ category, enabled, instructions, priority }]
export const getPreferences = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/profile/preferences/`, { headers: authHeaders() });
    return res.data.preferences || [];
  } catch (err) { on401(err); throw err; }
};

// PUT /api/profile/preferences/ — upsert list (non-destructive per category)
export const savePreferences = async (preferences) => {
  try {
    const res = await axios.put(`${BASE_URL}/profile/preferences/`, { preferences }, { headers: authHeaders() });
    return res.data.preferences || [];
  } catch (err) { on401(err); throw err; }
};

// GET /api/profile/signals/ → { suggestions, activity, top_keywords, top_stores }
export const getSignals = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/profile/signals/`, { headers: authHeaders() });
    return res.data || {};
  } catch (err) { on401(err); throw err; }
};

// POST /api/profile/signals/dismiss/ { signal_key }
export const dismissSignal = async (signalKey) => {
  try {
    const res = await axios.post(`${BASE_URL}/profile/signals/dismiss/`, { signal_key: signalKey }, { headers: authHeaders() });
    return res.data;
  } catch (err) { on401(err); throw err; }
};

// GET /api/profile/memberships/ → [{ store, tier, note }]
export const getMemberships = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/profile/memberships/`, { headers: authHeaders() });
    return res.data.memberships || [];
  } catch (err) { on401(err); throw err; }
};

// PUT /api/profile/memberships/ — REPLACES the full set (unlisted stores deleted)
export const saveMemberships = async (memberships) => {
  try {
    const res = await axios.put(`${BASE_URL}/profile/memberships/`, { memberships }, { headers: authHeaders() });
    return res.data.memberships || [];
  } catch (err) { on401(err); throw err; }
};

// GET /api/weekly-summary/ → [{ id, task_text, category }] completed in last 7 days
export const getWeeklySummary = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/weekly-summary/`, { headers: authHeaders() });
    return res.data.tasks || [];
  } catch (err) { on401(err); throw err; }
};

// ── mobile catalogs (kept in sync with InterestsSheet / MembershipsSheet) ──
export const CATEGORY_CATALOG = [
  { key: 'groceries', label: 'Groceries', icon: '🛒', examples: ['organic only', 'lactose-free', 'gluten-free', 'low sugar'] },
  { key: 'restaurants', label: 'Restaurants & Food', icon: '🍕', examples: ['vegetarian', 'spicy', 'late-night delivery', 'family-friendly'] },
  { key: 'pharmacy', label: 'Pharmacy & Health', icon: '💊', examples: ['allergy-safe', 'name-brand', 'fragrance-free'] },
  { key: 'apparel', label: 'Apparel & Fashion', icon: '👕', examples: ['size M', 'eco-friendly', 'no fast fashion', 'denim only'] },
  { key: 'home', label: 'Home & Furniture', icon: '🏠', examples: ['mid-century', 'kid-safe', 'mattress firm only'] },
  { key: 'electronics', label: 'Electronics', icon: '📱', examples: ['Apple ecosystem', 'gaming gear', 'noise cancelling'] },
  { key: 'travel', label: 'Travel', icon: '✈️', examples: ['window seats', 'pet-friendly hotels', 'aisle preferred'] },
  { key: 'beauty', label: 'Beauty & Wellness', icon: '💆', examples: ['cruelty-free', 'natural ingredients', 'fragrance-free'] },
  { key: 'entertainment', label: 'Entertainment', icon: '🎮', examples: ['indie games', 'classic films', 'live music'] },
  { key: 'kids', label: 'Kids & Family', icon: '🧸', examples: ['age 4–7', 'educational toys', 'safe for sensitive skin'] },
];

export const MEMBERSHIP_CATALOG = [
  { key: 'costco', label: 'Costco', icon: '🛒', tiers: [{ key: 'basic', label: 'Gold Star' }, { key: 'executive', label: 'Executive' }] },
  { key: 'sams_club', label: "Sam's Club", icon: '🛍️', tiers: [{ key: 'basic', label: 'Club' }, { key: 'plus', label: 'Plus' }] },
  { key: 'walmart_plus', label: 'Walmart+', icon: '🚚' },
  { key: 'amazon_prime', label: 'Amazon Prime', icon: '📦' },
  { key: 'target_circle', label: 'Target Circle', icon: '🎯' },
  { key: 'kroger_plus', label: 'Kroger Plus', icon: '🥖' },
  { key: 'cvs_extracare', label: 'CVS ExtraCare', icon: '💊' },
  { key: 'starbucks_rewards', label: 'Starbucks Rewards', icon: '☕' },
];
